// Copyright (c) 2026, Orgain and contributors
// For license information, please see license.txt

/**
 * LlmModelDialog - native in-app editor for an "LLM Model" record.
 *
 * Replaces the Frappe desk deep-link (/app/llm-model/new). Lets an admin
 * register a model (id, provider, type, capabilities, limits, pricing)
 * without leaving the SPA. Autonamed after model_id, so re-saving the same
 * id updates in place.
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Loader2, Save, ChevronDown } from 'lucide-react';

import {
  brainConfigService,
  type LlmProviderSummary,
} from '@/services/kbService';
import { logger } from '@/utils/logger';

const MODEL_TYPES = ['Text', 'Image', 'Video', 'Embedding', 'Multimodal'] as const;

interface FormState {
  model_id: string;
  model_name: string;
  provider: string;
  model_type: string;
  enabled: boolean;
  context_window: string;
  max_tokens: string;
  supports_function_calling: boolean;
  supports_vision: boolean;
  supports_streaming: boolean;
  cost_in: string;
  cost_out: string;
  rate_limit_rpm: string;
  rate_limit_tpm: string;
  description: string;
}

const emptyForm = (): FormState => ({
  model_id: '',
  model_name: '',
  provider: '',
  model_type: 'Text',
  enabled: true,
  context_window: '',
  max_tokens: '',
  supports_function_calling: false,
  supports_vision: false,
  supports_streaming: true,
  cost_in: '',
  cost_out: '',
  rate_limit_rpm: '',
  rate_limit_tpm: '',
  description: '',
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lowercased LLM Provider record key to pre-select (e.g. 'openai', 'google'). */
  defaultProviderKey?: string;
  /** Called with the saved model name so the caller can refresh. */
  onSaved: (name: string) => void;
}

export default function LlmModelDialog({
  open,
  onOpenChange,
  defaultProviderKey,
  onSaved,
}: Props) {
  const [providers, setProviders] = useState<LlmProviderSummary[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const patch = (p: Partial<FormState>) => setForm((prev) => ({ ...prev, ...p }));

  const init = useCallback(async () => {
    const fresh = emptyForm();
    try {
      const list = await brainConfigService.listLlmProviders();
      setProviders(list);
      const match = defaultProviderKey
        ? list.find((p) => p.name.toLowerCase() === defaultProviderKey)
        : undefined;
      fresh.provider = match?.name || (list.length === 1 ? list[0].name : '');
    } catch (err) {
      logger.error('Failed to list LLM providers', err);
      setProviders([]);
    }
    setForm(fresh);
  }, [defaultProviderKey]);

  useEffect(() => {
    if (open) void init();
  }, [open, init]);

  const handleSave = async () => {
    if (!form.model_id.trim()) {
      toast.warning('Model ID is required.');
      return;
    }
    if (!form.provider) {
      toast.warning('Pick a provider.');
      return;
    }
    setSaving(true);
    try {
      const result = await brainConfigService.saveLlmModel({
        model_id: form.model_id.trim(),
        provider: form.provider,
        model_name: form.model_name.trim() || undefined,
        model_type: form.model_type,
        enabled: form.enabled,
        context_window: form.context_window ? Number(form.context_window) : undefined,
        max_tokens: form.max_tokens ? Number(form.max_tokens) : undefined,
        supports_function_calling: form.supports_function_calling,
        supports_vision: form.supports_vision,
        supports_streaming: form.supports_streaming,
        cost_per_million_input_tokens: form.cost_in ? Number(form.cost_in) : undefined,
        cost_per_million_output_tokens: form.cost_out ? Number(form.cost_out) : undefined,
        rate_limit_rpm: form.rate_limit_rpm ? Number(form.rate_limit_rpm) : undefined,
        rate_limit_tpm: form.rate_limit_tpm ? Number(form.rate_limit_tpm) : undefined,
        description: form.description.trim() || undefined,
      });
      if (!result.ok || !result.name) {
        toast.error(result.error || 'Could not save the model');
        return;
      }
      toast.success('Model saved');
      onSaved(result.name);
      onOpenChange(false);
    } catch (err) {
      logger.error('Failed to save LLM model', err);
      toast.error('Could not save the model');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark max-w-xl max-h-[85vh] flex flex-col overflow-hidden bg-background text-foreground">
        <DialogHeader>
          <DialogTitle>Add LLM Model</DialogTitle>
          <DialogDescription>
            Register a model so it appears in the routing pickers. The Model ID is
            the wire identifier the provider expects (e.g. gpt-4o-mini).
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase text-muted-foreground">Model ID</Label>
                <Input
                  value={form.model_id}
                  onChange={(e) => patch({ model_id: e.target.value })}
                  placeholder="gpt-4o-mini"
                  disabled={saving}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-muted-foreground">Display Name</Label>
                <Input
                  value={form.model_name}
                  onChange={(e) => patch({ model_name: e.target.value })}
                  placeholder="GPT-4o mini"
                  disabled={saving}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-muted-foreground">Provider</Label>
                <Select value={form.provider} onValueChange={(v) => patch({ provider: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {providers.map((p) => (
                      <SelectItem key={p.name} value={p.name}>
                        {p.provider_name || p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-muted-foreground">Type</Label>
                <Select value={form.model_type} onValueChange={(v) => patch({ model_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {MODEL_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border p-2">
              <Label className="text-xs uppercase text-muted-foreground">Enabled</Label>
              <Switch
                checked={form.enabled}
                onCheckedChange={(c) => patch({ enabled: c })}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* Advanced - capabilities, limits, pricing */}
        <Collapsible>
          <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ChevronDown className="h-3 w-3" />
            Advanced (capabilities, limits, pricing)
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="grid gap-2 md:grid-cols-3">
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <Label className="text-xs">Function calling</Label>
                    <Switch
                      checked={form.supports_function_calling}
                      onCheckedChange={(c) => patch({ supports_function_calling: c })}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <Label className="text-xs">Vision</Label>
                    <Switch
                      checked={form.supports_vision}
                      onCheckedChange={(c) => patch({ supports_vision: c })}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <Label className="text-xs">Streaming</Label>
                    <Switch
                      checked={form.supports_streaming}
                      onCheckedChange={(c) => patch({ supports_streaming: c })}
                    />
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {(
                    [
                      ['context_window', 'Context window'],
                      ['max_tokens', 'Max tokens'],
                      ['cost_in', 'Cost / 1M input'],
                      ['cost_out', 'Cost / 1M output'],
                      ['rate_limit_rpm', 'Rate limit (RPM)'],
                      ['rate_limit_tpm', 'Rate limit (TPM)'],
                    ] as const
                  ).map(([field, label]) => (
                    <div className="space-y-1" key={field}>
                      <Label className="text-xs uppercase text-muted-foreground">{label}</Label>
                      <Input
                        type="number"
                        value={form[field]}
                        onChange={(e) => patch({ [field]: e.target.value } as Partial<FormState>)}
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => patch({ description: e.target.value })}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !form.model_id.trim() || !form.provider}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save model
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
